import { FormProvider, useFieldArray, UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "../../core/translation.core";
import { Product, ProductFormData, ProductSearchRst2 } from "../../types/product";
import { FancyCheckbox, LabelAndInput } from "../reuseables/form.comp";
import TrashIcon from "../../icons/trash.icon";
import { useQuery } from "@tanstack/react-query";
import productCore, { getProduct } from "../../core/product.core";
import localeCore from "../../core/locale.core";
import { useEffect, useRef, useState } from "react";
import ChevronDownIcon from "../../icons/chevronDown.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import { CurrentProduct, Panel } from "./boxConversionEditor";
import PlusIcon from "../../icons/plus.icon";
import CloseIcon from "../../icons/iconClose.comp";
import { inputCssNoMy } from "../reuseables/native.comp";

interface BoxConversionEditorProps {
  methods: UseFormReturn<any>;
}

export default function BundleProductEditor({ methods }: { methods: UseFormReturn<ProductFormData> }) {
  const { t, msg } = useTranslation();
  const {
    fields: budleProducts,
    append,
    remove,
  } = useFieldArray({
    control: methods.control,
    name: "bundleProducts",
  });

  useEffect(() => {
    if (budleProducts.length === 0) {
      append({ productId: "", quantity: 0, allowedVariationIds: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4" id="bundle-products">
      <div className="flex gap-2 items-center">
        <h3 className="text-base font-semibold">{t(msg.product.bundleProducts)}</h3>
        <button
          id="btn-add-bundle-product"
          type="button"
          onClick={() => append({ productId: "", quantity: 0, allowedVariationIds: [] })}
          className="flex space-x-2 py-2 rounded-md items-center"
        >
          <PlusIcon className="w-4 h-4" />
        </button>
      </div>
      {budleProducts?.map((p, i) => (
        <div key={i} className="space-y-4 bundle-product-row">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <ProductSearchSelector methods={methods} index={i} />
            </div>
            <div className="flex-1">
              <LabelAndInput
                register={methods.register}
                fieldName={`bundleProducts.${i}.quantity`}
                label={t(msg.product.quantity)}
                type="number"
                step="1"
                registerOptions={{ min: 1 }}
                labelCss="text-xs"
              />
            </div>
            <button
              type="button"
              onClick={() => remove(i)}
              className="mb-2 text-red-600 hover:text-red-800 delete-btn"
              title={t(msg.product.removeBoxConversion)}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
          <AllowedVariationsSelector methods={methods} index={i} />
        </div>
      ))}
    </div>
  );
}

function ProductSearchSelector({ methods, index }: { methods: UseFormReturn<ProductFormData>; index: number }) {
  const [isOpened, setIsOpened] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const ref = useRef<HTMLDivElement>(null);
  const { t, msg } = useTranslation();

  const productId = useWatch({ control: methods.control, name: `bundleProducts.${index}.productId` });

  const { data: products = { list: [], size: 0 } as ProductSearchRst2 } = useQuery({
    queryKey: [searchText],
    queryFn: (context) => productCore.searchProduct(context.queryKey[0] as string),
    enabled: searchText !== "",
  });

  useEffect(() => {
    function onClick(e: MouseEvent) {
      // This is a hack to get the latest state
      if (!ref || !ref.current) return;
      const isOpened = (ref.current as any).getAttribute("data-state");

      if (!isOpened) return;

      const isWithin = (e.target as any).closest(`#bundleProducts-productId-${index}`);

      if (!isWithin) {
        setIsOpened(false);
      }
    }

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [index]);

  const saveProduct = (productId: string) => {
    methods.setValue(`bundleProducts.${index}.productId` as any, productId);
    methods.setValue(`bundleProducts.${index}.allowedVariationIds` as any, []);
  };

  const css =
    "relative " +
    "h-9 w-full " +
    "dark:bg-her-backblue " +
    `border rounded-t-md ${
      !isOpened ? "rounded-b-md" : ""
    } hover:border-blue-200 hover:border-2 focus-visible:border-blue-600 focus-visible:border-2 ` +
    "focus-visible:outline-none outline-none " +
    "focus-visible:shadow-sm focus-visible:shadow-blue-800 " +
    "duration-1000 transition-[box-shadow] " +
    "disabled:text-her-lightgray2 text-base ";

  return (
    <div className="basis-full gap-2">
      <h2>{t(msg.product.filterProducts)}</h2>
      <FormProvider {...methods}>
        <div id={`bundleProducts-productId-${index}`} ref={ref} data-state={isOpened} className={css}>
          <button
            type="button"
            className="w-full h-full flex justify-between pr-2 pl-4 items-center"
            onClick={() => setIsOpened((value) => !value)}
          >
            <CurrentProduct productId={productId} /> <ChevronDownIcon />
          </button>
          {isOpened && <Panel products={products.list} onSearch={(value) => setSearchText(value)} saveProduct={saveProduct} />}
        </div>
      </FormProvider>
    </div>
  );
}

function AllowedVariationsSelector({ methods, index }: { methods: UseFormReturn<ProductFormData>; index: number }) {
  const { t, msg } = useTranslation();

  const productId = useWatch({ control: methods.control, name: `bundleProducts.${index}.productId` });
  const allowedVariationIds = useWatch({ control: methods.control, name: `bundleProducts.${index}.allowedVariationIds` }) || [];

  const { data: product, isLoading } = useQuery({
    queryKey: ["bundleProduct", productId],
    queryFn: () => getProduct(productId),
    enabled: !!productId,
  });

  if (!productId) return null;
  if (isLoading) return <LoadingOvalCenterIcon />;
  if (!product || product.productType !== "variationParent" || !product.variations || product.variations.length === 0) {
    return null;
  }

  const variations = product.variations || [];

  return (
    <div id={`bundleProducts-allowedVariations-${index}`} className="w-full">
      <p className="block text-xs font-semibold mb-2">{t(msg.product.allowedVariations)}</p>
      <div>
        {variations.map((variation: Product) => (
          <FancyCheckbox
            key={variation.id}
            value={variation.id}
            register={methods.register(`bundleProducts.${index}.allowedVariationIds` as any)}
            // defaultChecked={allowedVariationIds.includes(variation.id)}
          >
            {variation.sku}
          </FancyCheckbox>
        ))}
      </div>
    </div>
  );
}
